import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { WebsiteService, Page, SupportedLanguage, LanguageOption } from '../../services/website.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MenuEditorComponent } from '../menu-editor/menu-editor.component';
import { NotificationService } from '../../services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-website-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MenuEditorComponent],
  templateUrl: './website-selector.component.html',
  styleUrls: ['./website-selector.component.scss']
})
export class WebsiteSelectorComponent implements OnInit, OnDestroy {
  domains: string[] = [];
  selectedDomain: string = '';
  pages: Page[] = [];
  sortedPages: Page[] = [];
  paginatedPages: Page[] = [];
  currentPage: number = 1;
  itemsPerPage: number = 20;
  totalPages: number = 1;
  newDomain: string = '';
  showAddForm: boolean = false;
  showAddPageModal = false;
  isSubmitting = false;
  newPageTitle = '';
  currentLocale: SupportedLanguage = 'en';
  availableLanguages: LanguageOption[] = [];

  // Duplicate modal state
  showDuplicateModal = false;
  duplicatePageToDuplicate: Page | null = null;
  duplicateType: 'same-language' | 'other-language' | 'other-domain' = 'same-language';
  duplicateTargetLanguage: string = '';
  duplicateTargetDomain: string = '';
  duplicateNewTitle: string = '';
  isDuplicating = false;

  // Delete modal state
  showDeleteModal = false;
  pageToDelete: Page | null = null;

  @ViewChild(MenuEditorComponent) menuEditor?: MenuEditorComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private websiteService: WebsiteService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService
  ) {
    // Remove this as we'll handle it in ngOnInit
    // this.selectedDomain = this.websiteService.getCurrentDomain();
  }

  async ngOnInit() {
    try {
      // Subscribe to global language changes
      this.websiteService.currentLanguage$
        .pipe(takeUntil(this.destroy$))
        .subscribe(async lang => {
          this.currentLocale = lang;
          // Reload pages for the new language
          if (this.selectedDomain) {
            this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
            this.sortAndPaginatePages();
          }
          this.menuEditor?.loadMenuStructure();
        });
      // Load available domains
      this.domains = await this.websiteService.getWebsites();
      
      // Subscribe to domain changes to load data
      this.websiteService.currentDomain$
        .pipe(takeUntil(this.destroy$))
        .subscribe(async domain => {
          if (domain) {
            this.selectedDomain = domain;
            this.pages = await this.websiteService.getPagesByDomain(domain, this.currentLocale);
            this.sortAndPaginatePages();
            await this.menuEditor?.loadMenuStructure();
            
            // Load available languages for this domain
            const settings = await this.websiteService.getWebsiteSettings(domain);
            this.availableLanguages = settings?.availableLanguages || [];
          }
        });

      // Get the stored domain
      const storedDomain = this.websiteService.getCurrentDomain();
      if (storedDomain && this.domains.includes(storedDomain)) {
        await this.websiteService.setCurrentDomain(storedDomain);
      } else if (this.domains.length > 0) {
        await this.websiteService.setCurrentDomain(this.domains[0]);
      }

      // Load available languages
      if (this.selectedDomain) {
        const settings = await this.websiteService.getWebsiteSettings(this.selectedDomain);
        this.availableLanguages = settings?.availableLanguages || [];
      }
    } catch (error) {
      console.error('Error loading websites:', error);
    }
  }

  async onWebsiteChange() {
    if (!this.selectedDomain) {
      this.pages = [];
      return;
    }

    try {
      await this.websiteService.setCurrentDomain(this.selectedDomain);
      // Pages will be loaded via domainChange$ subscription
      await this.router.navigate(['/pages']);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  }

  editPage(page: Page) {
    // Navigate to editor with page ID
    this.router.navigate(['/pages', page.id]);
  }

  async addDomain() {
    if (!this.newDomain) return;
    
    try {
      // Create home page for all available languages
      const settings = await this.websiteService.getWebsiteSettings(this.newDomain);
      const languages = settings?.availableLanguages || [{ code: 'en', name: 'English' }];
      
      for (const langOption of languages) {
        const lang = langOption.code as SupportedLanguage;
        await this.websiteService.createPage(
          this.newDomain,
          'Home',
          '/',
          lang,
          0
        );
      }
      this.domains = await this.websiteService.getWebsites();
      this.newDomain = '';
      this.showAddForm = false;
    } catch (error) {
      console.error('Error creating domain:', error);
    }
  }

  async addPage() {
    if (!this.selectedDomain || !this.newPageTitle.trim()) return;
    
    try {
      this.isSubmitting = true;
      // Generate route from page name
      const route = this.generateRoute(this.newPageTitle);
      
      // Create page only for current language (not in menu by default, order = -1)
      const pageId = await this.websiteService.createPage(
        this.selectedDomain,
        this.newPageTitle,
        route,
        this.currentLocale,
        -1 // Not in menu by default
      );
      
      // Reset form
      this.newPageTitle = '';
      this.showAddPageModal = false;
      
      // Refresh pages for current language
      this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
      this.sortAndPaginatePages();
      
      this.notificationService.show({
        message: 'Pagina succesvol aangemaakt',
        type: 'success'
      });
    } catch (error) {
      console.error('Error creating page:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  // Add helper method to generate routes
  private generateRoute(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')         // Replace spaces with hyphens
      .replace(/-+/g, '-')          // Replace multiple hyphens with single hyphen
      .trim();                      // Remove leading/trailing spaces
  }

  openDeleteModal(page: Page) {
    this.pageToDelete = page;
    this.showDeleteModal = true;
  }

  async confirmDelete() {
    if (!this.pageToDelete) {
      return;
    }

    try {
      await this.websiteService.deletePage(this.selectedDomain, this.pageToDelete.id);
      this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
      
      this.notificationService.show({
        message: `Pagina "${this.pageToDelete.title}" succesvol verwijderd`,
        type: 'success'
      });

      // Close modal and reset
      this.showDeleteModal = false;
      this.pageToDelete = null;
    } catch (error) {
      console.error('Error deleting page:', error);
      this.notificationService.show({
        message: 'Pagina verwijderen mislukt',
        type: 'error'
      });
    }
  }

  async   togglePublished(page: Page) {
    try {
      const newPublishedStatus = page.published !== false ? false : true;
      
      // Update the page with new published status
      const updatedPage = await this.websiteService.getPageById(page.id);
      if (updatedPage) {
        await this.websiteService.updatePage({
          ...updatedPage,
          published: newPublishedStatus,
          updatedAt: new Date()
        });
      }

      // Reload pages for current language
      if (this.selectedDomain) {
        this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
        this.sortAndPaginatePages();
      }
      
      // Reload menu structure if menu editor exists
      await this.menuEditor?.loadMenuStructure();

      this.notificationService.show({
        message: newPublishedStatus ? 'Pagina gepubliceerd' : 'Pagina verborgen',
        type: 'success'
      });
    } catch (error) {
      console.error('Error toggling published status:', error);
      this.notificationService.show({
        message: 'Status bijwerken mislukt',
        type: 'error'
      });
    }
  }

  async toggleHomepage(page: Page) {
    try {
      if (!this.selectedDomain) return;

      if (page.isHomepage === true) {
        // Unset homepage
        await this.websiteService.unsetHomepage(page.id);
        this.notificationService.show({
          message: 'Homepage status verwijderd',
          type: 'success'
        });
      } else {
        // Set as homepage (this will automatically unset other homepages)
        await this.websiteService.setHomepage(page.id, this.selectedDomain, this.currentLocale);
        this.notificationService.show({
          message: 'Pagina ingesteld als homepage',
          type: 'success'
        });
      }

      // Reload pages
      if (this.selectedDomain) {
        this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
        this.sortAndPaginatePages();
      }
    } catch (error) {
      console.error('Error toggling homepage status:', error);
      this.notificationService.show({
        message: 'Homepage status bijwerken mislukt',
        type: 'error'
      });
    }
  }

  async addToMenu(page: Page) {
    try {
      await this.websiteService.updatePageOrder(page.id, 999999, null);
      // Reload pages for current language
      if (this.selectedDomain) {
        this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
        this.sortAndPaginatePages();
      }
      await this.menuEditor?.loadMenuStructure();
    } catch (error) {
      console.error('Error adding page to menu:', error);
    }
  }

  async onMenuChanged() {
    await this.onWebsiteChange();
    // Force menu editor to refresh if it exists
    const menuEditor = document.querySelector('app-menu-editor');
    if (menuEditor) {
      await (menuEditor as any).loadMenuStructure?.();
    }
  }

  // Add helper method
  private async loadPages() {
    if (this.selectedDomain) {
      this.pages = await this.websiteService.getPagesByDomain(this.selectedDomain, this.currentLocale);
      this.sortAndPaginatePages();
      await this.menuEditor?.loadMenuStructure();
    }
  }

  sortAndPaginatePages(): void {
    // Sort pages alphabetically
    this.sortedPages = [...this.pages].sort((a, b) => 
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    );

    // Calculate pagination
    this.totalPages = Math.ceil(this.sortedPages.length / this.itemsPerPage);
    
    // Reset to page 1 if current page is out of bounds
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }

    // Get pages for current page
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedPages = this.sortedPages.slice(startIndex, endIndex);
  }

  changeItemsPerPage(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 1;
    this.sortAndPaginatePages();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.sortAndPaginatePages();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Expose Math.min to template
  Math = Math;

  openDuplicateModal(page: Page) {
    this.duplicatePageToDuplicate = page;
    this.showDuplicateModal = true;
    this.duplicateNewTitle = page.title ? `${page.title} (kopie)` : '';
    this.duplicateTargetLanguage = '';
    this.duplicateTargetDomain = '';
    this.duplicateType = 'same-language';
  }

  async duplicatePage() {
    if (!this.duplicatePageToDuplicate || !this.duplicateNewTitle.trim()) {
      return;
    }

    if (this.duplicateType === 'other-language' && !this.duplicateTargetLanguage) {
      this.notificationService.show({
        message: 'Selecteer een taal om naar te dupliceren',
        type: 'error'
      });
      return;
    }

    if (this.duplicateType === 'other-domain' && !this.duplicateTargetDomain) {
      this.notificationService.show({
        message: 'Selecteer een domein om naar te kopiëren',
        type: 'error'
      });
      return;
    }

    try {
      this.isDuplicating = true;

      const currentDomain = this.websiteService.getCurrentDomain();
      if (!currentDomain) {
        this.notificationService.show({
          message: 'Geen domein geselecteerd',
          type: 'error'
        });
        return;
      }

      // Load full page data with all content
      const sourcePage = await this.websiteService.getPageById(this.duplicatePageToDuplicate.id);
      if (!sourcePage) {
        this.notificationService.show({
          message: 'Bronpagina niet gevonden',
          type: 'error'
        });
        return;
      }

      // Determine target domain and language
      const targetDomain = this.duplicateType === 'other-domain'
        ? this.duplicateTargetDomain
        : currentDomain;

      const targetLanguage = this.duplicateType === 'other-language'
        ? (this.duplicateTargetLanguage as SupportedLanguage)
        : this.duplicateType === 'other-domain'
          ? sourcePage.language  // Behoud de originele taal bij cross-domain kopie
          : this.currentLocale;

      // Generate route from new title
      const newRoute = this.generateRoute(this.duplicateNewTitle);

      // Get all pages for the target domain and language to calculate order
      const existingPages = await this.websiteService.getPagesByDomain(targetDomain, targetLanguage);
      const maxOrder = existingPages.length > 0
        ? Math.max(...existingPages.map(p => p.order))
        : -1;
      const newOrder = maxOrder >= 0 ? maxOrder + 1 : -1;

      // Determine basePageId - link to original if duplicating to other language, otherwise undefined
      const basePageId = this.duplicateType === 'other-language'
        ? (sourcePage.basePageId || sourcePage.id)
        : undefined;

      // Create new page on the target domain
      const newPageId = await this.websiteService.createPage(
        targetDomain,
        this.duplicateNewTitle,
        newRoute,
        targetLanguage,
        newOrder,
        basePageId
      );

      // Copy content to the new page
      if (this.duplicateType === 'other-domain') {
        // For cross-domain copies, use copyPageContent which bypasses domain check
        await this.websiteService.copyPageContent(newPageId, sourcePage);
      } else {
        // For same-domain copies, use the regular updatePage
        const newPage = await this.websiteService.getPageById(newPageId);
        if (newPage) {
          const updateData: any = {
            ...newPage,
            content: sourcePage.content || '',
            updatedAt: new Date()
          };

          if (sourcePage.components && sourcePage.components.length > 0) {
            updateData.components = JSON.parse(JSON.stringify(sourcePage.components));
          }
          if (sourcePage.templateType) {
            updateData.templateType = sourcePage.templateType;
          }
          if (sourcePage.categories && sourcePage.categories.length > 0) {
            updateData.categories = [...sourcePage.categories];
          }

          await this.websiteService.updatePage(updateData);
        }
      }

      // Build success message before resetting state
      const successMsg = this.duplicateType === 'other-domain'
        ? `Pagina succesvol gekopieerd naar ${targetDomain}`
        : 'Pagina succesvol gedupliceerd';

      // Close modal and reset form
      this.showDuplicateModal = false;
      this.duplicatePageToDuplicate = null;
      this.duplicateNewTitle = '';
      this.duplicateTargetLanguage = '';
      this.duplicateTargetDomain = '';
      this.duplicateType = 'same-language';

      // Refresh pages list (only updates current domain view)
      this.pages = await this.websiteService.getPagesByDomain(currentDomain, this.currentLocale);
      this.sortAndPaginatePages();

      this.notificationService.show({
        message: successMsg,
        type: 'success'
      });

    } catch (error) {
      console.error('Error duplicating page:', error);
      this.notificationService.show({
        message: 'Pagina dupliceren mislukt',
        type: 'error'
      });
    } finally {
      this.isDuplicating = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 